import { useState, useEffect } from 'react';
import { ProgressBar } from '@app/components/shared/ProgressBar';
import { CustomDropdown } from '@app/components/shared/CustomDropdown';
import { clearStepData, loadStepData, saveStepData } from '@/utils/localStorage';
import { validateBudgetRange } from '@/utils/budgetValidation';
import { OCCASION_OPTIONS, RELATIONSHIP_OPTIONS, SIZE_OPTIONS } from '@config/formOptions';
import { getMinDate } from '@/utils/dateConstraints';
import { buttonStyles, inputStyles, labelStyles, cardStyles, pageStyles, recipientCardStyles } from '@/styles/sharedStyles';

interface Step4Props {
  onNext: (data: any) => void;
  onBack?: () => void;
  onStepClick?: (step: number) => void;
  stepData?: any;
  flowType?: 'one-off' | 'subscription';
}

interface RecipientDetail {
  name: string;
  relationship: string;
  customRelationship?: string;
  budgetMin: number;
  budgetMax: number;
  sizes: {
    tops: string;
    bottoms: string;
    shoes: string;
  };
  interests: string;
  upcomingEvents: string;
  bestGift: string;
  comments?: string;
  showComments?: boolean;
}

interface EventBudget {
  occasion: string;
  customOccasion?: string;
  date: string;
  budgetMin: number;
  budgetMax: number;
  comments: string;
  showComments: boolean;
}

interface SubscriptionEvent {
  occasion: string;
  customOccasion?: string;
  date: string;
}

interface StepRecipient {
  name: string;
  relationship: string;
  customRelationship?: string;
  budgetMin: number;
  budgetMax: number;
}

export function Step4({ onNext, onBack, onStepClick, stepData, flowType = 'one-off' }: Step4Props) {
  const isSubscription = flowType === 'subscription';

  // Load data from localStorage if stepData is not provided or is empty
  const getEffectiveStepData = () => {
    if (stepData && Object.keys(stepData).length > 0) {
      return stepData;
    }
    
    // Load from localStorage as fallback
    const savedData = loadStepData(flowType);
    return savedData;
  };

  const effectiveStepData = getEffectiveStepData();

  // For one-off: get recipients from step1
  const recipients = isSubscription ? [] : (effectiveStepData?.step1?.recipients || []);
  
  // For subscription: get single recipient and events from step1
  const subscriptionRecipient = isSubscription ? effectiveStepData?.step1?.recipient : null;
  const subscriptionEvents = isSubscription ? (effectiveStepData?.step1?.events || []) : [];
  
  const [recipientDetails, setRecipientDetails] = useState<RecipientDetail[]>(() => {
    if (isSubscription) {
      // Subscription: single recipient from step1
      const initialRecipient = [{
        name: subscriptionRecipient?.name || '',
        relationship: subscriptionRecipient?.relationship || '',
        customRelationship: subscriptionRecipient?.customRelationship || '',
        budgetMin: subscriptionRecipient?.budgetMin || 0,
        budgetMax: subscriptionRecipient?.budgetMax || 0,
        sizes: { tops: '', bottoms: '', shoes: '' },
        interests: '',
        upcomingEvents: '',
        bestGift: '',
        comments: '',
        showComments: false
      }];
      return initialRecipient;
    } else {
      // One-off: multiple recipients from step1 - ensure at least one empty recipient if none exist
      const mappedRecipients = recipients.length > 0 ? 
        recipients.map((recipient: StepRecipient) => ({
          name: recipient.name || '',
          relationship: recipient.relationship || '',
          customRelationship: recipient.customRelationship || '',
          budgetMin: recipient.budgetMin || 0,
          budgetMax: recipient.budgetMax || 0,
          sizes: { tops: '', bottoms: '', shoes: '' },
          interests: '',
          upcomingEvents: '',
          bestGift: '',
          comments: '',
          showComments: false
        })) :
        [{ // Default empty recipient if no data
          name: '',
          relationship: '',
          customRelationship: '',
          budgetMin: 0,
          budgetMax: 0,
          sizes: { tops: '', bottoms: '', shoes: '' },
          interests: '',
          upcomingEvents: '',
          bestGift: '',
          comments: '',
          showComments: false
        }];
      return mappedRecipients;
    }
  });

  const [eventBudgets, setEventBudgets] = useState<EventBudget[]>(() => {
    const budgets = isSubscription ? 
      subscriptionEvents.filter((event: SubscriptionEvent) => event.occasion && event.date).map((event: SubscriptionEvent) => ({
        occasion: event.occasion,
        customOccasion: event.customOccasion || '',
        date: event.date,
        budgetMin: 50,
        budgetMax: null,
        comments: '',
        showComments: false
      })) : [];
      
    return budgets;
  });

  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string}>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  const updateRecipientDetail = (index: number, field: string, value: string | number | boolean) => {
    setRecipientDetails(prev => {
      const updated = [...prev];
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        updated[index] = {
          ...updated[index],
          [parent]: {
            ...(updated[index][parent as keyof RecipientDetail] as object),
            [child]: value
          }
        };
      } else {
        updated[index] = { ...updated[index], [field]: value };
      }
      return updated;
    });
  };

  const updateEventBudget = (index: number, field: keyof EventBudget, value: string | number | boolean) => {
    setEventBudgets(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const validateForm = (showErrors = false) => {
    const errors: {[key: string]: string} = {};
    let isValid = true;

    // Validate recipient details
    recipientDetails.forEach((recipient, index) => {
      // Name and relationship are now read-only, no validation needed
      
      /* COMMENTED OUT - Validation for editable name and relationship fields
      // Required: Name
      if (!recipient.name.trim()) {
        errors[`${index}-name`] = 'Recipient name is required';
        isValid = false;
      }

      // Required: Relationship
      if (!recipient.relationship.trim()) {
        errors[`${index}-relationship`] = 'Relationship is required';
        isValid = false;
      }
      
      // Required: Custom relationship if "Other" is selected
      if (recipient.relationship === 'Other' && !recipient.customRelationship?.trim()) {
        errors[`${index}-customRelationship`] = 'Please specify the relationship';
        isValid = false;
      }
      */

      // Required: Sizes (all three) - for both flows
      if (!recipient.sizes.tops.trim()) {
        errors[`${index}-sizes-tops`] = 'Top size is required';
        isValid = false;
      }
      if (!recipient.sizes.bottoms.trim()) {
        errors[`${index}-sizes-bottoms`] = 'Bottom size is required';
        isValid = false;
      }
      if (!recipient.sizes.shoes.trim()) {
        errors[`${index}-sizes-shoes`] = 'Shoe size is required';
        isValid = false;
      }

      // For one-off flow: validate budget
      if (!isSubscription) {
        // Required: Budget (min and max) using reusable utility
        const budgetValidation = validateBudgetRange(recipient.budgetMin, recipient.budgetMax);
        if (!budgetValidation.isValid) {
          if (budgetValidation.minError) {
            errors[`${index}-budgetMin`] = budgetValidation.minError;
            isValid = false;
          }
          if (budgetValidation.maxError) {
            errors[`${index}-budgetMax`] = budgetValidation.maxError;
            isValid = false;
          }
        }
      }

      // Optional fields - no validation needed for interests, upcomingEvents, bestGift
    });

    // For subscription flow: validate events
    if (isSubscription) {
      eventBudgets.forEach((event, index) => {
        // Occasion and date are now read-only, no validation needed
        
        /* COMMENTED OUT - Validation for editable occasion and date fields
        // Required: Occasion
        if (!event.occasion.trim()) {
          errors[`event-${index}-occasion`] = 'Occasion is required';
          isValid = false;
        }
        
        // Required: Custom occasion if "Other" or "Holiday" is selected
        if ((event.occasion === 'Other' || event.occasion === 'Holiday') && !event.customOccasion?.trim()) {
          errors[`event-${index}-customOccasion`] = event.occasion === 'Holiday' ? 'Please specify the holiday' : 'Please specify the occasion';
          isValid = false;
        }

        // Required: Date
        if (!event.date.trim()) {
          errors[`event-${index}-date`] = 'Date is required';
          isValid = false;
        }
        */

        // Required: Budget (min and max) using reusable utility
        const budgetValidation = validateBudgetRange(event.budgetMin, event.budgetMax);
        if (!budgetValidation.isValid) {
          if (budgetValidation.minError) {
            errors[`event-${index}-budgetMin`] = budgetValidation.minError;
            isValid = false;
          }
          if (budgetValidation.maxError) {
            errors[`event-${index}-budgetMax`] = budgetValidation.maxError;
            isValid = false;
          }
        }
      });
    }

    if (showErrors) {
      setFieldErrors(errors);
      setShowValidationErrors(true);
    }

    return isValid;
  };

  // Update form validity when data changes
  useEffect(() => {
    const valid = validateForm();
    setIsFormValid(valid);
  }, [recipientDetails, eventBudgets]);

  // No need to save Step4 data - we clear localStorage when finishing the flow

  const handleFinishClick = async () => {
    if (!validateForm(true)) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Process eventBudgets to clean up unnecessary fields
      const processedEventBudgets = eventBudgets.map(event => ({
        occasion: event.occasion,
        customOccasion: event.customOccasion,
        date: event.date,
        budgetMin: event.budgetMin,
        budgetMax: event.budgetMax,
        comments: event.comments,
        // Remove showComments (UI-only) and otherHolidayOccasion (not needed)
      }));
      
      // Clear localStorage since the flow is finishing
      clearStepData(flowType);
      
      await onNext({ recipientDetails, eventBudgets: processedEventBudgets });
    } catch (error) {
      console.error('Error in handleFinishClick:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisabledButtonClick = () => {
    // Show validation errors when clicking disabled button
    validateForm(true);
  };

  const handleSwitchToCall = () => {
    // Navigate to Calendly booking
    if (flowType === 'subscription') {
      window.location.href = '/subscription/booking';
    } else {
      window.location.href = '/one-off/booking';
    }
  };

  if (isSubscription) {
    // Subscription Layout
    return (
      <div className="max-w-7xl w-full mx-auto">
        <div className="mb-8">
          <ProgressBar currentStep={4} totalSteps={4} onStepClick={onStepClick} />
        </div>

        {/* Header with Switch to Call */}
        <div className={`${cardStyles.base} bg-gray-50 mb-8 flex items-center justify-between`}>
          <div className="flex items-center gap-4">
            <div className="text-4xl">📞</div>
            <div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Switch to call instead</h3>
              <p className="text-gray-600">Is it easier to give us a rundown over the phone? The Birdies love to chat. Let's get a quick call scheduled so we can get shopping for you.</p>
            </div>
          </div>
          <button 
            className={buttonStyles.primary}
            onClick={handleSwitchToCall}
          >
            SWITCH TO CALL →
          </button>
        </div>

        <div className="grid lg:grid-cols-[350px_1fr] gap-8">
          {/* Left Column */}
          <div>
            <h2 className={pageStyles.sectionTitle}>Tell Us About Your Gifting Calendar</h2>
            <p className="text-gray-600 mb-6">
              Fill in the key details for each celebration and we'll take care of the rest. Welcome to the world of effortless gifting-get ready to take all the credit!
            </p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
              <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-white text-sm">ℹ</span>
              </div>
              <p className="text-blue-800 text-sm">
                You have to fill all the fields, it is important for us to have all the details, you can switch to a call instead if you prefer.
              </p>
            </div>
          </div>

          {/* Right Column */}
          <div>
            <h2 className={`${pageStyles.sectionTitle} mb-6`}>Recipient Details</h2>
            
            {/* Single Recipient */}
            {recipientDetails.map((recipient, index) => (
              <div key={index} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelStyles.base}>Recipient name</label>
                    <input
                      type="text"
                      value={recipient.name}
                      readOnly
                      className={`${inputStyles.base} bg-gray-100 cursor-not-allowed`}
                    />
                    {/* COMMENTED OUT - Editable recipient name field
                    <input
                      type="text"
                      value={recipient.name}
                      onChange={(e) => updateRecipientDetail(index, 'name', e.target.value)}
                      placeholder="Enter a name"
                      className={`${inputStyles.base} ${showValidationErrors && fieldErrors[`${index}-name`] ? inputStyles.error : ''}`}
                    />
                    {showValidationErrors && fieldErrors[`${index}-name`] && (
                      <p className="text-sm text-red-600 mt-1">{fieldErrors[`${index}-name`]}</p>
                    )}
                    */}
                  </div>
                  <div>
                    <label className={labelStyles.base}>Relationship</label>
                    <input
                      type="text"
                      value={recipient.customRelationship || recipient.relationship}
                      readOnly
                      className={`${inputStyles.base} bg-gray-100 cursor-not-allowed`}
                    />
                    {/* COMMENTED OUT - Editable relationship field
                    <CustomDropdown
                      value={recipient.relationship}
                      onChange={(selectedRelationship) => updateRecipientDetail(index, 'relationship', selectedRelationship)}
                      options={RELATIONSHIP_OPTIONS}
                      placeholder="Select relationship"
                      className={`${showValidationErrors && fieldErrors[`${index}-relationship`] ? 'border-red-500' : ''}`}
                    />
                    {showValidationErrors && fieldErrors[`${index}-relationship`] && (
                      <p className="text-sm text-red-600 mt-1">{fieldErrors[`${index}-relationship`]}</p>
                    )}
                    */}
                  </div>
                </div>

                {/* COMMENTED OUT - Custom relationship field
                {recipient.relationship === 'Other' && (
                  <div>
                    <label className={`${labelStyles.base} ${labelStyles.required}`}>Specify relationship</label>
                    <input
                      type="text"
                      value={recipient.customRelationship || ''}
                      onChange={(e) => updateRecipientDetail(index, 'customRelationship', e.target.value)}
                      placeholder="e.g., Cousin, Neighbor, Colleague"
                      className={`${inputStyles.base} ${showValidationErrors && fieldErrors[`${index}-customRelationship`] ? inputStyles.error : ''}`}
                    />
                    {showValidationErrors && fieldErrors[`${index}-customRelationship`] && (
                      <p className="text-sm text-red-600 mt-1">{fieldErrors[`${index}-customRelationship`]}</p>
                    )}
                  </div>
                )}
                */}


                {/* Sizing Fields */}
                <div>
                  <h3 className="text-lg font-medium text-gray-700 mb-4">Sizes</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className={`${labelStyles.base} ${labelStyles.required}`}>Tops</label>
                      <CustomDropdown
                        value={recipient.sizes.tops}
                        onChange={(selectedSize) => updateRecipientDetail(index, 'sizes.tops', selectedSize)}
                        options={SIZE_OPTIONS.TOPS}
                        placeholder="Select size"
                        className={`${showValidationErrors && fieldErrors[`${index}-sizes-tops`] ? 'border-red-500' : ''}`}
                      />
                      {showValidationErrors && fieldErrors[`${index}-sizes-tops`] && (
                        <p className="text-sm text-red-600 mt-1">{fieldErrors[`${index}-sizes-tops`]}</p>
                      )}
                    </div>
                    <div>
                      <label className={`${labelStyles.base} ${labelStyles.required}`}>Bottoms</label>
                      <CustomDropdown
                        value={recipient.sizes.bottoms}
                        onChange={(selectedSize) => updateRecipientDetail(index, 'sizes.bottoms', selectedSize)}
                        options={SIZE_OPTIONS.BOTTOMS}
                        placeholder="Select size"
                        className={`${showValidationErrors && fieldErrors[`${index}-sizes-bottoms`] ? 'border-red-500' : ''}`}
                      />
                      {showValidationErrors && fieldErrors[`${index}-sizes-bottoms`] && (
                        <p className="text-sm text-red-600 mt-1">{fieldErrors[`${index}-sizes-bottoms`]}</p>
                      )}
                    </div>
                    <div>
                      <label className={`${labelStyles.base} ${labelStyles.required}`}>Shoes</label>
                      <CustomDropdown
                        value={recipient.sizes.shoes}
                        onChange={(selectedSize) => updateRecipientDetail(index, 'sizes.shoes', selectedSize)}
                        options={SIZE_OPTIONS.SHOES}
                        placeholder="Select size"
                        className={`${showValidationErrors && fieldErrors[`${index}-sizes-shoes`] ? 'border-red-500' : ''}`}
                      />
                      {showValidationErrors && fieldErrors[`${index}-sizes-shoes`] && (
                        <p className="text-sm text-red-600 mt-1">{fieldErrors[`${index}-sizes-shoes`]}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Text areas - Optional fields */}
                <div>
                  <label className={labelStyles.base}>Recipient Interests</label>
                  <textarea
                    value={recipient.interests}
                    onChange={(e) => updateRecipientDetail(index, 'interests', e.target.value)}
                    placeholder="Into books, baking, biking, or something else entirely? Share a few clues about their style, hobbies, or personality, and we'll tailor gift ideas that feel personal, not generic."
                    className={`${inputStyles.base} min-h-24`}
                  />
                </div>

                <div>
                  <label className={labelStyles.base}>Any upcoming events or trips?</label>
                  <textarea
                    value={recipient.upcomingEvents}
                    onChange={(e) => updateRecipientDetail(index, 'upcomingEvents', e.target.value)}
                    placeholder="Think: a winter trip to a cozy cabin, a long-awaited girls' weekend, an exciting career change, or even a big move into a new home. We will help you gift with intention."
                    className={`${inputStyles.base} min-h-24`}
                  />
                </div>

                <div>
                  <label className={labelStyles.base}>What is the best gift you have ever given this person?</label>
                  <textarea
                    value={recipient.bestGift}
                    onChange={(e) => updateRecipientDetail(index, 'bestGift', e.target.value)}
                    placeholder="We want to know what excites them. What's a gift they've loved in the past? Do they prefer practical gifts or fun surprises? With this information, we can dial in the perfect way to surprise and delight!"
                    className={`${inputStyles.base} min-h-24`}
                  />
                </div>
              </div>
            ))}

            {/* Events Budgets Section */}
            <div className="mt-12">
              <h2 className={`${pageStyles.sectionTitle} mb-6`}>Events Budgets</h2>
              
              {eventBudgets.map((event, index) => (
                <div key={index} className={`${recipientCardStyles.container} mb-6`}>
                  <div className={recipientCardStyles.badge}>Event {index + 1}</div>
                  
                  {/* First Row: Occasion and Date - READ ONLY */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className={labelStyles.base}>What's the occasion?</label>
                      <input
                        type="text"
                        value={event.customOccasion || event.occasion}
                        readOnly
                        className={`${inputStyles.base} bg-gray-100 cursor-not-allowed`}
                      />
                      {/* COMMENTED OUT - Editable occasion field
                      <select
                        value={event.occasion}
                        onChange={(e) => updateEventBudget(index, 'occasion', e.target.value)}
                        className={`${inputStyles.select} ${showValidationErrors && fieldErrors[`event-${index}-occasion`] ? inputStyles.error : ''}`}
                      >
                        <option value="">Select occasion</option>
                        {OCCASION_OPTIONS.map(option => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                      {showValidationErrors && fieldErrors[`event-${index}-occasion`] && (
                        <p className="text-sm text-red-600 mt-1">{fieldErrors[`event-${index}-occasion`]}</p>
                      )}
                      */}
                    </div>
                    <div>
                      <label className={labelStyles.base}>Date</label>
                      <input
                        type="text"
                        value={new Date(event.date).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                        readOnly
                        className={`${inputStyles.base} bg-gray-100 cursor-not-allowed`}
                      />
                      {/* COMMENTED OUT - Editable date field
                      <input
                        type="date"
                        value={event.date}
                        min={getMinDate()}
                        onChange={(e) => updateEventBudget(index, 'date', e.target.value)}
                        className={`${inputStyles.base} ${showValidationErrors && fieldErrors[`event-${index}-date`] ? inputStyles.error : ''}`}
                      />
                      {showValidationErrors && fieldErrors[`event-${index}-date`] && (
                        <p className="text-sm text-red-600 mt-1">{fieldErrors[`event-${index}-date`]}</p>
                      )}
                      */}
                    </div>
                  </div>

                  {/* COMMENTED OUT - Custom Occasion and Holiday Inputs
                  {event.occasion === 'Other' && (
                    <div className="mb-4">
                      <label className={`${labelStyles.base} ${labelStyles.required}`}>Specify occasion</label>
                      <input
                        type="text"
                        value={event.customOccasion || ''}
                        onChange={(e) => updateEventBudget(index, 'customOccasion', e.target.value)}
                        placeholder="e.g., Graduation, Promotion"
                        className={`${inputStyles.base} ${showValidationErrors && fieldErrors[`event-${index}-customOccasion`] ? inputStyles.error : ''}`}
                      />
                      {showValidationErrors && fieldErrors[`event-${index}-customOccasion`] && (
                        <p className="text-sm text-red-600 mt-1">{fieldErrors[`event-${index}-customOccasion`]}</p>
                      )}
                    </div>
                  )}

                  {event.occasion === 'Holiday' && (
                    <div className="mb-4">
                      <label className={`${labelStyles.base} ${labelStyles.required}`}>Specify holiday</label>
                      <input
                        type="text"
                        value={event.customOccasion || ''}
                        onChange={(e) => updateEventBudget(index, 'customOccasion', e.target.value)}
                        placeholder="e.g., Christmas, Hanukkah, Thanksgiving"
                        className={`${inputStyles.base} ${showValidationErrors && fieldErrors[`event-${index}-customOccasion`] ? inputStyles.error : ''}`}
                      />
                      {showValidationErrors && fieldErrors[`event-${index}-customOccasion`] && (
                        <p className="text-sm text-red-600 mt-1">{fieldErrors[`event-${index}-customOccasion`]}</p>
                      )}
                    </div>
                  )}
                  */}

                  {/* Second Row: Budget Min and Max */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className={`${labelStyles.base} ${labelStyles.required}`}>Budget Min</label>
                      <input
                        type="number"
                        value={event.budgetMin || ''}
                        onChange={(e) => updateEventBudget(index, 'budgetMin', parseInt(e.target.value) || 0)}
                        placeholder="80"
                        className={`${inputStyles.base} ${showValidationErrors && fieldErrors[`event-${index}-budgetMin`] ? inputStyles.error : ''}`}
                      />
                      {showValidationErrors && fieldErrors[`event-${index}-budgetMin`] && (
                        <p className="text-sm text-red-600 mt-1">{fieldErrors[`event-${index}-budgetMin`]}</p>
                      )}
                    </div>
                    <div>
                      <label className={`${labelStyles.base} ${labelStyles.required}`}>Budget Max</label>
                      <input
                        type="number"
                        value={event.budgetMax || ''}
                        onChange={(e) => updateEventBudget(index, 'budgetMax', parseInt(e.target.value) || 0)}
                        placeholder="180"
                        className={`${inputStyles.base} ${showValidationErrors && fieldErrors[`event-${index}-budgetMax`] ? inputStyles.error : ''}`}
                      />
                      {showValidationErrors && fieldErrors[`event-${index}-budgetMax`] && (
                        <p className="text-sm text-red-600 mt-1">{fieldErrors[`event-${index}-budgetMax`]}</p>
                      )}
                    </div>
                  </div>


                  {/* Comments Section - Hidden by default */}
                  {event.showComments && (
                    <div className="mb-4">
                      <label className={labelStyles.base}>Do you want to leave specific comments for this event?</label>
                      <textarea
                        value={event.comments}
                        onChange={(e) => updateEventBudget(index, 'comments', e.target.value)}
                        placeholder="Is there a story or memory tied to this date that makes it extra special? Are there traditions you share around this celebration? What kind of vibe do you want the gift to carry—comfort, celebration, encouragement, surprise? We love details that might help us shape the perfect gift."
                        className={`${inputStyles.base} min-h-24`}
                      />
                    </div>
                  )}

                  {/* Show More/Less Button */}
                  <div className="mt-4 text-center">
                    <button 
                      className="text-purple-medium text-sm font-medium cursor-pointer hover:text-purple-dark"
                      onClick={() => updateEventBudget(index, 'showComments', !event.showComments)}
                    >
                      {event.showComments ? 'SHOW LESS -' : 'SHOW MORE +'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Buttons for Subscription */}
        <div className="flex gap-4 justify-center pt-6">
          <button 
            className={buttonStyles.secondary}
            onClick={handleSwitchToCall}
          >
            SWITCH TO CALL INSTEAD
          </button>
          <button 
            className={isFormValid && !isLoading ? buttonStyles.primary : buttonStyles.disabled}
            onClick={isFormValid && !isLoading ? handleFinishClick : handleDisabledButtonClick}
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                PROCESSING...
              </span>
            ) : 'FINISH'}
          </button>
        </div>
        
        {/* Processing Warning Message */}
        {isLoading && (
          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg max-w-md mx-auto">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-yellow-800">
                  Please don't close this page
                </p>
                <p className="text-xs text-yellow-700 mt-1">
                  Your details are being saved. This may take a few seconds to a couple of minutes. Closing this page may result in data loss.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // One-off Layout
  return (
    <div className="max-w-7xl w-full mx-auto">
      <div className="mb-8">
        <ProgressBar currentStep={4} totalSteps={4} onStepClick={onStepClick} />
      </div>

      <div className="grid lg:grid-cols-[350px_1fr] gap-8">
        {/* Left Column - Switch to Call */}
        <div className={`${cardStyles.base} bg-gray-50 h-fit`}>
          <div className="text-center mb-6">
            <div className="text-6xl mb-4">📞</div>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Switch to call instead</h3>
            <p className="text-gray-600 text-sm leading-relaxed mb-6">
              Is it easier to give us a rundown over the phone? The Birdies love to chat. Let's get a quick call scheduled so we can get shopping for you.
            </p>
            <button 
              className={`${buttonStyles.primary} w-full`}
              onClick={handleSwitchToCall}
            >
              SWITCH TO CALL →
            </button>
          </div>
        </div>

        {/* Right Column - Recipients */}
        <div>
          <div className="mb-8">
            <h1 className={pageStyles.title}>Recipient Details</h1>
            <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto">
              Once you complete this form, we'll have everything we need to create a thoughtful, personalized gift list for your recipients.
            </p>
          </div>

          {recipientDetails.map((recipient, index) => (
            <div key={index} className={`${recipientCardStyles.container} mb-6`}>
              <div className={recipientCardStyles.badge}>Recipient {index + 1}</div>
              
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className={labelStyles.base}>Recipient {index + 1} name</label>
                  <input
                    type="text"
                    value={recipient.name}
                    readOnly
                    className={`${inputStyles.base} bg-gray-100 cursor-not-allowed`}
                  />
                  {/* COMMENTED OUT - Editable recipient name field
                  <input
                    type="text"
                    value={recipient.name}
                    onChange={(e) => updateRecipientDetail(index, 'name', e.target.value)}
                    placeholder="Enter a name"
                    className={`${inputStyles.base} ${showValidationErrors && fieldErrors[`${index}-name`] ? inputStyles.error : ''}`}
                  />
                  {showValidationErrors && fieldErrors[`${index}-name`] && (
                    <p className="text-sm text-red-600 mt-1">{fieldErrors[`${index}-name`]}</p>
                  )}
                  */}
                </div>
                <div>
                  <label className={labelStyles.base}>Relationship</label>
                  <input
                    type="text"
                    value={recipient.customRelationship || recipient.relationship}
                    readOnly
                    className={`${inputStyles.base} bg-gray-100 cursor-not-allowed`}
                  />
                  {/* COMMENTED OUT - Editable relationship field
                  <CustomDropdown
                    value={recipient.relationship}
                    onChange={(selectedRelationship) => updateRecipientDetail(index, 'relationship', selectedRelationship)}
                    options={RELATIONSHIP_OPTIONS}
                    placeholder="Select relationship"
                    className={`${showValidationErrors && fieldErrors[`${index}-relationship`] ? 'border-red-500' : ''}`}
                  />
                  {showValidationErrors && fieldErrors[`${index}-relationship`] && (
                    <p className="text-sm text-red-600 mt-1">{fieldErrors[`${index}-relationship`]}</p>
                  )}
                  */}
                </div>
              </div>

              {/* COMMENTED OUT - Custom relationship field
              {recipient.relationship === 'Other' && (
                <div className="mb-6">
                  <label className={`${labelStyles.base} ${labelStyles.required}`}>Specify relationship</label>
                  <input
                    type="text"
                    value={recipient.customRelationship || ''}
                    onChange={(e) => updateRecipientDetail(index, 'customRelationship', e.target.value)}
                    placeholder="e.g., Cousin, Neighbor, Colleague"
                    className={`${inputStyles.base} ${showValidationErrors && fieldErrors[`${index}-customRelationship`] ? inputStyles.error : ''}`}
                  />
                  {showValidationErrors && fieldErrors[`${index}-customRelationship`] && (
                    <p className="text-sm text-red-600 mt-1">{fieldErrors[`${index}-customRelationship`]}</p>
                  )}
                </div>
              )}
              */}


              {/* Budget Section */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className={`${labelStyles.base} ${labelStyles.required}`}>Budget Min</label>
                  <input
                    type="number"
                    value={recipient.budgetMin || ''}
                    onChange={(e) => updateRecipientDetail(index, 'budgetMin', parseInt(e.target.value) || 0)}
                    placeholder="80"
                    className={`${inputStyles.base} ${showValidationErrors && fieldErrors[`${index}-budgetMin`] ? inputStyles.error : ''}`}
                  />
                  {showValidationErrors && fieldErrors[`${index}-budgetMin`] && (
                    <p className="text-sm text-red-600 mt-1">{fieldErrors[`${index}-budgetMin`]}</p>
                  )}
                </div>
                <div>
                  <label className={`${labelStyles.base} ${labelStyles.required}`}>Budget Max</label>
                  <input
                    type="number"
                    value={recipient.budgetMax || ''}
                    onChange={(e) => updateRecipientDetail(index, 'budgetMax', parseInt(e.target.value) || 0)}
                    placeholder="180"
                    className={`${inputStyles.base} ${showValidationErrors && fieldErrors[`${index}-budgetMax`] ? inputStyles.error : ''}`}
                  />
                  {showValidationErrors && fieldErrors[`${index}-budgetMax`] && (
                    <p className="text-sm text-red-600 mt-1">{fieldErrors[`${index}-budgetMax`]}</p>
                  )}
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">Do you know this person's sizes?</label>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className={`${labelStyles.base} ${labelStyles.required}`}>Tops</label>
                    <CustomDropdown
                      value={recipient.sizes.tops}
                      onChange={(selectedSize) => updateRecipientDetail(index, 'sizes.tops', selectedSize)}
                      options={SIZE_OPTIONS.TOPS}
                      placeholder="Select size"
                      className={`${showValidationErrors && fieldErrors[`${index}-sizes-tops`] ? 'border-red-500' : ''}`}
                    />
                    {showValidationErrors && fieldErrors[`${index}-sizes-tops`] && (
                      <p className="text-sm text-red-600 mt-1">{fieldErrors[`${index}-sizes-tops`]}</p>
                    )}
                  </div>
                  <div>
                    <label className={`${labelStyles.base} ${labelStyles.required}`}>Bottoms</label>
                    <CustomDropdown
                      value={recipient.sizes.bottoms}
                      onChange={(selectedSize) => updateRecipientDetail(index, 'sizes.bottoms', selectedSize)}
                      options={SIZE_OPTIONS.BOTTOMS}
                      placeholder="Select size"
                      className={`${showValidationErrors && fieldErrors[`${index}-sizes-bottoms`] ? 'border-red-500' : ''}`}
                    />
                    {showValidationErrors && fieldErrors[`${index}-sizes-bottoms`] && (
                      <p className="text-sm text-red-600 mt-1">{fieldErrors[`${index}-sizes-bottoms`]}</p>
                    )}
                  </div>
                  <div>
                    <label className={`${labelStyles.base} ${labelStyles.required}`}>Shoes</label>
                    <CustomDropdown
                      value={recipient.sizes.shoes}
                      onChange={(selectedSize) => updateRecipientDetail(index, 'sizes.shoes', selectedSize)}
                      options={SIZE_OPTIONS.SHOES}
                      placeholder="Select size"
                      className={`${showValidationErrors && fieldErrors[`${index}-sizes-shoes`] ? 'border-red-500' : ''}`}
                    />
                    {showValidationErrors && fieldErrors[`${index}-sizes-shoes`] && (
                      <p className="text-sm text-red-600 mt-1">{fieldErrors[`${index}-sizes-shoes`]}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <label className={labelStyles.base}>Interests</label>
                  <textarea
                    value={recipient.interests}
                    onChange={(e) => updateRecipientDetail(index, 'interests', e.target.value)}
                    placeholder="Into books, baking, biking, or something else entirely? Share a few clues about their style, hobbies, or personality, and we'll tailor gift ideas that feel personal, not generic."
                    className={`${inputStyles.base} min-h-24`}
                  />
                </div>

                <div>
                  <label className={labelStyles.base}>Any upcoming events or trips?</label>
                  <textarea
                    value={recipient.upcomingEvents}
                    onChange={(e) => updateRecipientDetail(index, 'upcomingEvents', e.target.value)}
                    placeholder="Think: a winter trip to a cozy cabin, a long-awaited girls' weekend, an exciting career change, or even a big move into a new home. We will help you gift with intention."
                    className={`${inputStyles.base} min-h-24`}
                  />
                </div>

                <div>
                  <label className={labelStyles.base}>What is the best gift you have ever given this person?</label>
                  <textarea
                    value={recipient.bestGift}
                    onChange={(e) => updateRecipientDetail(index, 'bestGift', e.target.value)}
                    placeholder="We want to know what excites them. What's a gift they've loved in the past? Do they prefer practical gifts or fun surprises? With this information, we can dial in the perfect way to surprise and delight!"
                    className={`${inputStyles.base} min-h-24`}
                  />
                </div>

                {/* Comments Section - Hidden by default */}
                {recipient.showComments && (
                  <div>
                    <label className={labelStyles.base}>Do you want to leave specific comments for this gift?</label>
                    <textarea
                      value={recipient.comments}
                      onChange={(e) => updateRecipientDetail(index, 'comments', e.target.value)}
                      placeholder="Is there a story or memory tied to this date that makes it extra special? Are there traditions you share around this celebration? What kind of vibe do you want the gift to carry—comfort, celebration, encouragement, surprise? We love details that might help us shape the perfect gift."
                      className={`${inputStyles.base} min-h-24`}
                    />
                  </div>
                )}

                {/* Show More/Less Button */}
                <div className="text-center">
                  <button 
                    type="button"
                    className="text-purple-medium text-sm font-medium cursor-pointer hover:text-purple-dark"
                    onClick={() => updateRecipientDetail(index, 'showComments', !recipient.showComments)}
                  >
                    {recipient.showComments ? 'SHOW LESS -' : 'SHOW MORE +'}
                  </button>
                </div>
              </div>
            </div>
          ))}

          <div className="flex gap-4 justify-center pt-6">
            <button 
              className={buttonStyles.secondary}
              onClick={handleSwitchToCall}
            >
              SWITCH TO CALL INSTEAD
            </button>
            <button 
              className={isFormValid && !isLoading ? buttonStyles.primary : buttonStyles.disabled}
              onClick={isFormValid && !isLoading ? handleFinishClick : handleDisabledButtonClick}
              disabled={isLoading}
            >
              {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                PROCESSING...
              </span>
            ) : 'FINISH'}
            </button>
          </div>
          
          {/* Processing Warning Message */}
          {isLoading && (
            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg max-w-md mx-auto">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-yellow-800">
                    Please don't close this page
                  </p>
                  <p className="text-xs text-yellow-700 mt-1">
                    Your details are being saved. This may take a few seconds to a couple of minutes. Closing this page may result in data loss.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}